import { useCallback, useEffect, useRef, useState } from "react";
import { getWsApiUrl } from "@/config/runtime";
import { getAccessTokenForWs } from "@/lib/api";

/**
 * Real-time voice-to-text hook using the Azure AI Foundry Realtime API via a
 * backend-proxied WebSocket.
 *
 * Audio is captured with the Web Audio API, downsampled to PCM16 mono @ 24 kHz
 * in an AudioWorklet, and streamed frame-by-frame to the backend, which relays
 * it to the realtime transcription model.
 *
 * Transcript model (deliberately simple + idempotent):
 * - The hook maintains the COMPLETE transcript for the current recording
 *   session and emits it in full via `onTranscript` on every update — never a
 *   delta. Consumers treat each call as "the dictation is now exactly this."
 * - `onStart` / `onEnd` bracket a session so consumers can snapshot/reset a
 *   base (see `useVoiceDictation`).
 * - There is no separate interim/final duality and no commit-on-close special
 *   case: the last `onTranscript` already carries the final text, so ordering
 *   of the server's `partial`/`final`/close events can't cause duplication or
 *   loss.
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Target sample rate for the Realtime API (must match the worklet + backend). */
const TARGET_SAMPLE_RATE = 24_000;

/** Path to the PCM downsampling worklet (served as a static asset). */
const PCM_WORKLET_URL = "/pcm-worklet.js";

/** WebSocket path on the backend transcription router. */
const WS_PATH = "/transcription/stream";

/** How often (ms) to sample the audio level for the visualizer. */
const AUDIO_LEVEL_INTERVAL_MS = 50;

/** Seconds of consecutive silence before auto-stopping. */
const SILENCE_TIMEOUT_SECONDS = 8;

/** Audio level below this threshold is considered silence (0–1 scale). */
const SILENCE_THRESHOLD = 0.02;

// ---------------------------------------------------------------------------
// Support detection
// ---------------------------------------------------------------------------

/**
 * Whether the browser supports the realtime pipeline: getUserMedia, WebSocket,
 * and AudioWorklet.
 */
export function isRealtimeVoiceSupported(): boolean {
  if (typeof globalThis === "undefined") return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  if (typeof WebSocket === "undefined") return false;
  if (typeof AudioContext === "undefined") return false;
  // AudioWorklet is on the AudioContext prototype; check lazily at start().
  return true;
}

export { SILENCE_THRESHOLD, SILENCE_TIMEOUT_SECONDS, TARGET_SAMPLE_RATE, WS_PATH };

// ---------------------------------------------------------------------------
// Hook types (unchanged public contract)
// ---------------------------------------------------------------------------

export interface UseVoiceInputOptions {
  /** Kept for API compatibility; realtime is always continuous. */
  continuous?: boolean;
  /**
   * Called on every update with the **complete** transcript for the current
   * recording session (not a delta). This is idempotent: the consumer should
   * treat it as "the dictated text is now exactly this" and render it
   * accordingly. Called repeatedly as the user speaks and once more when the
   * session finalizes.
   */
  onTranscript?: (sessionTranscript: string) => void;
  /** Called when a recording session starts (mic acquired, socket open). */
  onStart?: () => void;
  /** Called when a recording session ends (stopped, closed, or errored). */
  onEnd?: () => void;
  /** Called when an error occurs. */
  onError?: (error: string) => void;
  /** Enable auto-stop after silence. Defaults to true. */
  autoStopOnSilence?: boolean;
}

export interface UseVoiceInputReturn {
  /** Whether the current browser supports voice input. */
  isSupported: boolean;
  /** Whether the microphone is actively recording. */
  isListening: boolean;
  /** Interim status/partial text while recording. */
  interimTranscript: string;
  /** Whether we are waiting on the connection to finalize after stop. */
  isTranscribing: boolean;
  /** Current audio input level (0–1 scale). */
  audioLevel: number;
  /** Elapsed recording time in seconds. */
  elapsedSeconds: number;
  /** Toggle listening on/off. */
  toggle: () => void;
  /** Explicitly start listening. */
  start: () => void;
  /** Explicitly stop listening. */
  stop: () => void;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const { onTranscript, onStart, onEnd, onError, autoStopOnSilence = true } = options;

  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const isSupported = isRealtimeVoiceSupported();

  // Stable callback refs.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Session refs.
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const silenceStartRef = useRef<number | null>(null);
  const wantListeningRef = useRef(false);
  const isStartingRef = useRef(false);
  // The COMPLETE transcript for the current recording session. gpt-realtime
  // streams incremental pieces via `partial` events; we accumulate them here
  // and always emit the full text, so `onTranscript` is idempotent ("the
  // dictation is now exactly this") rather than a fragile delta/replace dance.
  const sessionTextRef = useRef("");

  // ── Teardown ────────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    if (audioLevelTimerRef.current !== null) {
      clearInterval(audioLevelTimerRef.current);
      audioLevelTimerRef.current = null;
    }
    if (durationTimerRef.current !== null) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setAudioLevel(0);
    setElapsedSeconds(0);
    silenceStartRef.current = null;
  }, []);

  // ── Stop ────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    setIsListening(false);

    // Tell the backend to flush + finalize; keep the socket open briefly so a
    // trailing transcript update can arrive, then it closes on its own.
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      setIsTranscribing(true);
      try {
        ws.send(JSON.stringify({ type: "stop" }));
      } catch {
        // ignore — socket may be mid-close
      }
    }
    // Stop capturing audio immediately (mic indicator off).
    cleanup();
    setInterimTranscript("");
  }, [cleanup]);

  const stopRef = useRef(stop);
  stopRef.current = stop;

  // ── WebSocket message handling ──────────────────────────────────────

  /** Emit the current full session transcript to the consumer (idempotent). */
  const emitTranscript = useCallback(() => {
    const text = sessionTextRef.current.trim();
    setInterimTranscript(text);
    onTranscriptRef.current?.(text);
  }, []);

  const handleServerMessage = useCallback(
    (data: string) => {
      let payload: { type?: string; text?: string; message?: string };
      try {
        payload = JSON.parse(data);
      } catch {
        return;
      }

      switch (payload.type) {
        case "partial":
          // Incremental piece — append to the running session transcript.
          sessionTextRef.current += payload.text ?? "";
          emitTranscript();
          break;
        case "final":
          // A finalized transcript replaces the accumulated text (server's
          // authoritative version). If empty, keep what we have.
          if (payload.text) sessionTextRef.current = payload.text;
          emitTranscript();
          break;
        case "error":
          onErrorRef.current?.(payload.message ?? "Transcription error.");
          break;
      }
    },
    [emitTranscript],
  );

  // ── Audio level metering + silence detection ────────────────────────

  const startMetering = useCallback(
    (analyser: AnalyserNode) => {
      const data = new Uint8Array(analyser.frequencyBinCount);
      audioLevelTimerRef.current = setInterval(() => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (const v of data) sum += v;
        const avg = sum / data.length / 255;
        setAudioLevel(avg);

        if (autoStopOnSilence && wantListeningRef.current) {
          if (avg < SILENCE_THRESHOLD) {
            if (silenceStartRef.current === null) {
              silenceStartRef.current = Date.now();
            } else if ((Date.now() - silenceStartRef.current) / 1000 >= SILENCE_TIMEOUT_SECONDS) {
              stopRef.current();
            }
          } else {
            silenceStartRef.current = null;
          }
        }
      }, AUDIO_LEVEL_INTERVAL_MS);
    },
    [autoStopOnSilence],
  );

  // ── Start ───────────────────────────────────────────────────────────

  const start = useCallback(async () => {
    if (!isRealtimeVoiceSupported()) return;
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    wantListeningRef.current = true;
    // Fresh session: clear any prior transcript and notify the consumer so it
    // can snapshot its base (e.g. the current input value).
    sessionTextRef.current = "";
    onStartRef.current?.();

    try {
      // 1. Acquire mic.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      if (!wantListeningRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      // 2. Open the backend WebSocket. Browsers can't set an Authorization
      //    header on a WebSocket, so the token rides the Sec-WebSocket-Protocol
      //    handshake as a second subprotocol ("bearer", <token>) — keeping it
      //    out of the URL (and access logs / history).
      const token = await getAccessTokenForWs();
      const url = `${getWsApiUrl()}${WS_PATH}`;
      const ws = new WebSocket(url, ["bearer", token]);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        if (typeof ev.data === "string") handleServerMessage(ev.data);
      };
      ws.onerror = () => {
        onErrorRef.current?.("Connection error. Please try again.");
      };
      ws.onclose = () => {
        setIsTranscribing(false);
        // The last transcript update already left the correct full text in the
        // consumer (idempotent). Just notify that the session ended.
        onEndRef.current?.();
        if (wsRef.current === ws) wsRef.current = null;
      };

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        // If it errors before opening, reject so we surface a clean message.
        const priorOnError = ws.onerror;
        ws.onerror = (e) => {
          priorOnError?.call(ws, e as Event);
          reject(new Error("WebSocket failed to open"));
        };
      });
      // Restore steady-state error handler.
      ws.onerror = () => onErrorRef.current?.("Connection error. Please try again.");

      // 3. Set up the audio graph: source → worklet (PCM out) + analyser.
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      await audioCtx.audioWorklet.addModule(PCM_WORKLET_URL);

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      analyserRef.current = analyser;
      source.connect(analyser);

      const worklet = new AudioWorkletNode(audioCtx, "pcm-processor");
      workletNodeRef.current = worklet;
      worklet.port.onmessage = (ev: MessageEvent<ArrayBuffer>) => {
        const socket = wsRef.current;
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(ev.data);
        }
      };
      source.connect(worklet);
      // Worklet doesn't produce output; connecting to destination keeps the
      // graph pulling. Use a zero-gain node to avoid echoing mic to speakers.
      const mute = audioCtx.createGain();
      mute.gain.value = 0;
      worklet.connect(mute).connect(audioCtx.destination);

      // 4. Start meters + timers.
      startMetering(analyser);
      setIsListening(true);
      setInterimTranscript("Listening…");
      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      durationTimerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      console.error("[useVoiceInput] Failed to start realtime transcription:", err);
      const msg =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Microphone access denied. Check browser permissions."
          : "Could not start voice input. Please try again.";
      onErrorRef.current?.(msg);
      wantListeningRef.current = false;
      setIsListening(false);
      setInterimTranscript("");
      cleanup();
      wsRef.current?.close();
      wsRef.current = null;
      // We already fired onStart; balance it so the consumer resets its base.
      onEndRef.current?.();
    } finally {
      isStartingRef.current = false;
    }
  }, [handleServerMessage, startMetering, cleanup]);

  // ── Toggle ──────────────────────────────────────────────────────────

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  // ── Cleanup on unmount ──────────────────────────────────────────────

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      cleanup();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [cleanup]);

  return {
    isSupported,
    isListening,
    interimTranscript,
    isTranscribing,
    audioLevel,
    elapsedSeconds,
    toggle,
    start,
    stop,
  };
}
